/**
 * A guard around the forms and cards under an answer (12 Oct).
 *
 * LIVE, 9 Oct: "I've opened the hardware request form", and no form showed.
 * A form that fails to draw must not take the conversation with it, nor fail
 * silently: the person gets the plain catalog link in its place and the
 * failure is logged for IT, like any screen that crashed.
 */

import { Alert } from "@norican/react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link } from "react-router";

import { useT } from "@/i18n";
import { reportClientError } from "@/lib/api/client-error-report";
import { logger } from "@/lib/logger";

import { ASSISTANT_FALLBACK_LINKS } from "./assistant-fallback";

interface FormErrorBoundaryProps {
  children: ReactNode;
}

interface FormErrorBoundaryState {
  failed: boolean;
}

export class FormErrorBoundary extends Component<FormErrorBoundaryProps, FormErrorBoundaryState> {
  public state: FormErrorBoundaryState = { failed: false };

  public static getDerivedStateFromError(): FormErrorBoundaryState {
    return { failed: true };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error(
      "[assistant] a form under the answer failed to render",
      error,
      info.componentStack,
    );
    reportClientError({
      kind: "RENDER",
      message: `Assistant form: ${error.name}: ${error.message}`,
    });
  }

  public render(): ReactNode {
    return this.state.failed ? <FormFallback /> : this.props.children;
  }
}

/** The plain request form, offered where the assistant's form could not be drawn. */
function FormFallback() {
  const { t } = useT("assistant");
  const { t: tCommon } = useT("common");
  const request = ASSISTANT_FALLBACK_LINKS.find((link) => link.key === "requestSomething");
  return (
    <Alert tone="warning" title={tCommon("state.errorTitle")}>
      {request === undefined ? null : (
        <Link to={request.path} className="text-accent underline">
          {t(`fallback.${request.key}`)}
        </Link>
      )}
    </Alert>
  );
}
